// ─── M3 Expressive Select — Select Component (Exposed Dropdown & Autocomplete) ──
import * as React from "react";
import { useVirtualList } from "../../hooks/useVirtualList";
import { cn } from "../../lib/utils";
import { Icon } from "../icon";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "../menu";
import { ProgressIndicator } from "../progress-indicator";
import { TextField } from "../text-field";

import type { TextFieldHandle } from "../text-field/text-field.types";
import type { SelectOption, SelectProps } from "./select.types";

/**
 * M3 Expressive Select component (Exposed Dropdown Menu & Autocomplete).
 *
 * Implements Material 3 Exposed Dropdown Menu spec (Figma Node `58966:3961`).
 * Features progressive slice virtualization and server-side/async search via `onSearchChange`.
 *
 * @example
 * // Standard Exposed Dropdown Menu
 * <Select
 *   label="City"
 *   options={[
 *     { label: "Hanoi", value: "hn" },
 *     { label: "Ho Chi Minh City", value: "hcm" }
 *   ]}
 *   value={city}
 *   onChange={(val) => setCity(val)}
 * />
 *
 * @example
 * // Searchable Autocomplete (Local filtering)
 * <Select
 *   searchable
 *   label="Search Employee"
 *   options={userList}
 *   onChange={(val, opt) => setSelected(opt)}
 * />
 *
 * @example
 * // Searchable Autocomplete (Async / Server-side search)
 * <Select
 *   searchable
 *   loading={isLoading}
 *   label="Search Countries"
 *   options={searchResults}
 *   onSearchChange={(query) => fetchCountries(query)}
 *   onChange={(val, opt) => setSelectedCountry(opt)}
 * />
 */
export const Select = React.forwardRef<TextFieldHandle, SelectProps>(
	(
		{
			options,
			value: controlledValue,
			defaultValue,
			onChange,
			onSearchChange,
			searchable = false,
			loading = false,
			maxVisible = 50,
			maxHeight = "360px",
			placeholder,
			label,
			supportingText,
			colorVariant = "standard",
			menuVariant = "expressive",
			showDividers = true,
			separatorStyle,
			emptyText = "No options found",
			matchTriggerWidth = true,
			disabled = false,
			className,
			variant = "outlined",
			renderOption,
			renderContent,
			children,
			...restProps
		},
		ref,
	) => {
		const [internalValue, setInternalValue] = React.useState(
			() => defaultValue ?? "",
		);
		const isControlled = controlledValue !== undefined;
		const currentValue = isControlled ? controlledValue : internalValue;

		const [open, setOpen] = React.useState(false);
		const openRef = React.useRef(open);
		openRef.current = open;

		const [searchQuery, setSearchQuery] = React.useState("");
		const [scrollViewport, setScrollViewport] =
			React.useState<HTMLDivElement | null>(null);

		// Find selected option object
		const selectedOption = React.useMemo(
			() => options.find((opt) => opt.value === currentValue),
			[options, currentValue],
		);

		// Determine display text for input
		const displayValue = searchable
			? open
				? searchQuery
				: (selectedOption?.label ?? "")
			: (selectedOption?.label ?? "");

		// Filter options:
		// When `onSearchChange` is provided, internal filtering is disabled (consumer passes filtered list)
		const filteredOptions = React.useMemo(() => {
			if (onSearchChange) return options;
			if (!searchable || !searchQuery.trim()) return options;
			const query = searchQuery.toLowerCase();
			return options.filter((opt) => opt.label.toLowerCase().includes(query));
		}, [options, searchable, searchQuery, onSearchChange]);

		// Progressive virtualization using IntersectionObserver with ScrollArea viewport as root
		const { visibleItems, hasMore, sentinelRef } = useVirtualList(
			filteredOptions,
			{
				batchSize: maxVisible,
				root: scrollViewport,
			},
		);

		const handleSelect = React.useCallback(
			(option: SelectOption) => {
				if (option.disabled) return;
				if (!isControlled) {
					setInternalValue(option.value);
				}
				onChange?.(option.value, option);
				setOpen(false);
				setSearchQuery("");
			},
			[isControlled, onChange],
		);

		const handleInputChange = React.useCallback(
			(inputValue: string) => {
				if (searchable) {
					setSearchQuery(inputValue);
					if (!openRef.current) setOpen(true);
					onSearchChange?.(inputValue);
				}
			},
			[searchable, onSearchChange],
		);

		const handleInputFocus = React.useCallback(() => {
			if (!disabled && !openRef.current) {
				setOpen(true);
				if (searchable) {
					setSearchQuery("");
					onSearchChange?.("");
				}
			}
		}, [disabled, searchable, onSearchChange]);

		const handleKeyDown = React.useCallback(
			(e: React.KeyboardEvent<HTMLInputElement>) => {
				if (e.key === "ArrowDown" && !openRef.current) {
					e.preventDefault();
					setOpen(true);
				} else if (e.key === "Escape" && openRef.current) {
					setOpen(false);
				}
			},
			[],
		);

		const resolvedSeparatorStyle =
			separatorStyle ?? (showDividers ? "divider" : "none");

		return (
			<Menu
				open={open}
				onOpenChange={setOpen}
				colorVariant={colorVariant}
				variant={menuVariant}
				modal={false}
			>
				<MenuTrigger asChild>
					<div
						className="w-full"
						onPointerDown={
							searchable
								? (e) => {
										// In searchable mode, the user is interacting with an editable text input.
										// Prevent Radix DropdownMenu.Trigger from intercepting pointerdown (which toggles open/closed
										// and calls preventDefault(), blocking text selection and native input focus).
										e.preventDefault();
										if (!disabled) {
											if (!openRef.current) {
												setOpen(true);
											}
											(e.target as HTMLElement)?.focus?.();
										}
									}
								: undefined
						}
					>
						<TextField
							ref={ref}
							variant={variant}
							label={label}
							supportingText={supportingText}
							placeholder={placeholder}
							disabled={disabled}
							readOnly={!searchable}
							value={displayValue}
							onChange={handleInputChange}
							onFocus={handleInputFocus}
							onKeyDown={handleKeyDown}
							className={cn(
								searchable ? "cursor-text" : "cursor-pointer",
								className,
							)}
							trailingIcon={
								<Icon
									name="arrow_drop_down"
									className={cn(
										"transition-transform duration-200 cursor-pointer",
										open && "rotate-180",
									)}
									onClick={
										searchable
											? (e) => {
													if (!disabled) {
														e.stopPropagation();
														setOpen((prev) => !prev);
													}
												}
											: undefined
									}
								/>
							}
							{...restProps}
						/>
					</div>
				</MenuTrigger>
				<MenuContent
					matchTriggerWidth={matchTriggerWidth}
					separatorStyle={resolvedSeparatorStyle}
					maxHeight={maxHeight}
					onViewportRef={setScrollViewport}
					className="p-1"
					onCloseAutoFocus={(e: Event) => {
						if (searchable) {
							e.preventDefault();
						}
					}}
					onFocusOutside={(e: Event) => {
						if (searchable) {
							e.preventDefault();
						}
					}}
				>
					{children ? (
						children
					) : renderContent ? (
						renderContent({
							options,
							filteredOptions,
							visibleOptions: visibleItems,
							hasMore,
							sentinelRef,
							currentValue,
							selectedOption,
							searchQuery,
							handleSelect,
							emptyText,
							loading,
						})
					) : loading ? (
						<SelectLoading />
					) : filteredOptions.length > 0 ? (
						<>
							{visibleItems.map((option, index) => {
								if (renderOption) {
									return (
										<React.Fragment key={option.value}>
											{renderOption(option, {
												selected: option.value === currentValue,
												disabled: option.disabled,
												handleSelect: () => handleSelect(option),
												index,
											})}
										</React.Fragment>
									);
								}
								return (
									<MenuItem
										key={option.value}
										selected={option.value === currentValue}
										disabled={option.disabled}
										leadingIcon={option.leadingIcon}
										supportingText={option.supportingText}
										onClick={() => handleSelect(option)}
									>
										{option.label}
									</MenuItem>
								);
							})}
							{hasMore && (
								<div
									ref={sentinelRef}
									className="h-2 w-full shrink-0 pointer-events-none"
									aria-hidden="true"
								/>
							)}
						</>
					) : (
						<SelectEmpty>{emptyText}</SelectEmpty>
					)}
				</MenuContent>
			</Menu>
		);
	},
);
Select.displayName = "Select";

function SelectLoading() {
	return (
		<div className="flex items-center justify-center p-4">
			<ProgressIndicator
				variant="circular"
				size={24}
				trackHeight={3}
				aria-label="Loading options..."
			/>
		</div>
	);
}

function SelectEmpty({
	children,
	isGapVariant: _isGapVariant,
	itemPosition: _itemPosition,
	index: _index,
	count: _count,
	...props
}: {
	children?: React.ReactNode;
	isGapVariant?: boolean;
	itemPosition?: string;
	index?: number;
	count?: number;
} & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			{...props}
			className="px-4 py-3 text-body-medium text-m3-on-surface-variant text-center select-none"
		>
			{children}
		</div>
	);
}
